import { __assign } from 'tslib';
import { Card, alpha } from '@mui/material';
import { styled } from '@mui/material/styles';
import isEmpty from 'lodash/isEmpty';
import { KMosaicVerticalTextAlignType, KMosaicTextBackgroundType, KMosaicHorizontalTextAlignType } from './index.props.js';

var PREFIX = "KMosaicElement";
var classes = {
    card: "".concat(PREFIX, "-card"),
    action: "".concat(PREFIX, "-action"),
    loader: "".concat(PREFIX, "-loader"),
    media: "".concat(PREFIX, "-media"),
    header: "".concat(PREFIX, "-header"),
    elementTitle: "".concat(PREFIX, "-elementTitle"),
    subTitle: "".concat(PREFIX, "-subTitle"),
    subTitleText: "".concat(PREFIX, "-subTitleText"),
    subTitleIcon: "".concat(PREFIX, "-subTitleIcon"),
    activeArrow: "".concat(PREFIX, "-activeArrow"),
    partialLoading: "".concat(PREFIX, "-partialLoading")
};
var defaultArrow = function (theme) {
    return {
        position: "absolute",
        borderTop: "".concat(theme.spacing(1.5), " solid ").concat(theme.palette.common.black),
        borderLeft: "".concat(theme.spacing(1.5), " solid transparent"),
        borderRight: "".concat(theme.spacing(1.5), " solid transparent"),
        left: "50%",
        marginLeft: theme.spacing(-1.5),
        bottom: theme.spacing(-1.5)
    };
};
var reversedArrow = function (theme, maxHeaderHeight) {
    return {
        position: "absolute",
        borderTop: "".concat(theme.spacing(1.5), " solid ").concat(theme.palette.grey[200]),
        borderLeft: "".concat(theme.spacing(1.5), " solid transparent"),
        borderRight: "".concat(theme.spacing(1.5), " solid transparent"),
        left: "50%",
        marginLeft: theme.spacing(-1.5),
        bottom: "calc(-".concat(maxHeaderHeight, "px - ").concat(theme.spacing(0.5), ")"),
        transform: "rotate(-180deg) !important"
    };
};
var StyledKMosaicElement = styled(Card, {
    shouldForwardProp: function (prop) {
        return prop !== "labelCustom" && prop !== "maxHeaderHeight" && prop !== "borderRadius";
    }
})(function (_a) {
    var _b;
    var theme = _a.theme, labelCustom = _a.labelCustom, maxHeaderHeight = _a.maxHeaderHeight, borderRadius = _a.borderRadius;
    return (_b = {},
        _b["&.".concat(classes.card)] = {
            overflow: "visible",
            width: "100%",
            paddingBottom: "100%",
            position: "relative",
            boxShadow: "none",
            borderRadius: borderRadius,
            "&:hover, &:focus-within": {
                boxShadow: "0px 3px 6px rgba(0, 0, 0, 0.16)"
            },
            "&:focus-within": {
                outline: "1px solid ".concat(theme.palette.primary.main),
                filter: "brightness(0.8)"
            },
            marginBottom: (labelCustom === null || labelCustom === void 0 ? void 0 : labelCustom.verticalTextAlign) === KMosaicVerticalTextAlignType.UnderMosaic
                ? "calc(".concat(maxHeaderHeight, "px - ").concat(theme.spacing(1.5), ")")
                : "hidden"
        },
        _b["& .".concat(classes.action)] = {
            position: "absolute",
            left: 0,
            top: 0,
            right: 0,
            bottom: 0,
            width: "100%",
            height: "100%",
            borderRadius: borderRadius || "inherit",
            overflow: (labelCustom === null || labelCustom === void 0 ? void 0 : labelCustom.verticalTextAlign) === KMosaicVerticalTextAlignType.UnderMosaic
                ? "visible"
                : "hidden"
        },
        _b["& .".concat(classes.loader)] = {
            marginBottom: "auto"
        },
        _b["& .".concat(classes.media)] = {
            display: "flex",
            flexDirection: "column",
            justifyContent: "flex-end",
            width: "100%",
            height: "100%",
            overflow: "hidden",
            borderRadius: borderRadius || "inherit"
        },
        _b["& .".concat(classes.header)] = __assign(__assign(__assign({}, (labelCustom &&
            !isEmpty(labelCustom) && {
            padding: labelCustom.verticalTextAlign === KMosaicVerticalTextAlignType.UnderMosaic
                ? theme.spacing(1, 1, 1, 1)
                : labelCustom.verticalTextAlign === KMosaicVerticalTextAlignType.Top
                    ? theme.spacing(1, 1, 1, 4)
                    : theme.spacing(4, 1, 1, 1),
            borderRadius: "0 0 ".concat(theme.shape.borderRadius, "px ").concat(theme.shape.borderRadius, "px"),
            borderBottomLeftRadius: borderRadius || "inherit",
            borderBottomRightRadius: borderRadius || "inherit",
            position: "absolute",
            width: "100%",
            top: labelCustom.verticalTextAlign === KMosaicVerticalTextAlignType.Top
                ? "0"
                : labelCustom.verticalTextAlign === KMosaicVerticalTextAlignType.Center
                    ? "50%"
                    : labelCustom.verticalTextAlign === KMosaicVerticalTextAlignType.UnderMosaic
                        ? "100%"
                        : "auto",
            bottom: labelCustom.verticalTextAlign === KMosaicVerticalTextAlignType.Bottom ? "0" : "auto",
            transform: labelCustom.verticalTextAlign === KMosaicVerticalTextAlignType.Center
                ? "translate(0%, -50%)"
                : "none",
            justifyContent: "center",
            background: labelCustom.backgroundTextEffect === KMosaicTextBackgroundType.NoBackground
                ? "transparent"
                : labelCustom.backgroundTextEffect === KMosaicTextBackgroundType.BottomEffect
                    ? "linear-gradient(rgba(0, 0, 0, 0) 0%, ".concat(labelCustom.backgroundTextColor, " 100%)")
                    : labelCustom.backgroundTextEffect === KMosaicTextBackgroundType.TopEffect
                        ? "linear-gradient(".concat(labelCustom.backgroundTextColor, " 0%, rgba(0, 0, 0, 0) 100%)")
                        : labelCustom.backgroundTextColor,
            display: "flex",
            flexDirection: "column"
        })), ((!labelCustom || isEmpty(labelCustom)) && {
            padding: theme.spacing(4, 1, 1, 1),
            background: "linear-gradient(".concat(alpha(theme.palette.common.black, 0), " 0%, ").concat(alpha(theme.palette.common.black, 0.87), " 100%)"),
            position: "relative"
        })), { height: maxHeaderHeight > 0 ? maxHeaderHeight : "auto" }),
        _b["& .".concat(classes.elementTitle)] = __assign(__assign({ lineHeight: 1, marginBottom: theme.spacing(1) }, (labelCustom &&
            !isEmpty(labelCustom) && {
            color: labelCustom.textColor || theme.palette.background.paper,
            fontSize: labelCustom.textSize || "1rem",
            textAlign: labelCustom.horizontalTextAlign === KMosaicHorizontalTextAlignType.Center
                ? "center"
                : labelCustom.horizontalTextAlign === KMosaicHorizontalTextAlignType.Right
                    ? "right"
                    : "left"
        })), ((!labelCustom || isEmpty(labelCustom)) && {
            color: theme.palette.background.paper,
            fontSize: "1rem"
        })),
        _b["& .".concat(classes.subTitle)] = __assign(__assign({ display: "flex", alignItems: "center" }, (labelCustom &&
            !isEmpty(labelCustom) && {
            color: labelCustom.textColor || theme.palette.background.paper,
            alignSelf: labelCustom.horizontalTextAlign === KMosaicHorizontalTextAlignType.Center
                ? "center"
                : labelCustom.horizontalTextAlign === KMosaicHorizontalTextAlignType.Right
                    ? "end"
                    : "baseline"
        })), ((!labelCustom || isEmpty(labelCustom)) && {
            color: theme.palette.background.paper
        })),
        _b["& .".concat(classes.subTitleText)] = {
            marginLeft: theme.spacing(1),
            fontSize: "0.8rem",
            lineHeight: 1
        },
        _b["& .".concat(classes.subTitleIcon)] = __assign(__assign({}, (labelCustom &&
            !isEmpty(labelCustom) && {
            color: labelCustom.textColor || theme.palette.background.paper
        })), ((!labelCustom || isEmpty(labelCustom)) && {
            color: theme.palette.background.paper
        })),
        _b["& .".concat(classes.activeArrow)] = (labelCustom === null || labelCustom === void 0 ? void 0 : labelCustom.verticalTextAlign) === KMosaicVerticalTextAlignType.UnderMosaic
            ? reversedArrow(theme, maxHeaderHeight)
            : defaultArrow(theme),
        _b["& .".concat(classes.partialLoading)] = {
            "& svg circle": __assign(__assign({}, (labelCustom &&
                !isEmpty(labelCustom) && {
                stroke: labelCustom.textColor || theme.palette.background.paper
            })), ((!labelCustom || isEmpty(labelCustom)) && {
                stroke: theme.palette.background.paper
            }))
        },
        _b);
});

export { classes, StyledKMosaicElement as default };
//# sourceMappingURL=index.styled.js.map
